import { api, ApiResponse } from './api';

export interface Task {
  task_id: string;
  title: string;
  patient_id: string;
  assigned_to: string | null;
  due_date: string; // YYYY-MM-DD
  priority: 'high' | 'medium' | 'low';
  category: 'follow_up_visit' | 'lab_review' | 'medication_check' | 'patient_checkin' | 'documentation' | 'other';
  source: 'manual' | 'ai_generated';
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  effective_status?: 'pending' | 'in_progress' | 'completed' | 'overdue';
  notes?: string;
  encounter_id?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
  };
  assignedProvider?: {
    id: string;
    first_name: string;
    last_name: string;
    credentials?: string;
  };
  encounter?: {
    id: string;
    encounter_date: string;
    chief_complaint?: string;
  };
}

export interface TaskSuggestion {
  title: string;
  suggested_due_date: string;
  priority: 'high' | 'medium' | 'low';
  category: 'follow_up_visit' | 'lab_review' | 'medication_check' | 'patient_checkin' | 'documentation' | 'other';
  reasoning: string;
}

export interface TaskSearchPatient {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  dob?: string | null;
  gender?: string | null;
  patient_id?: string | null;
}

export interface TaskSuggestionsResponse {
  success: boolean;
  data: {
    encounter_id: string;
    patient_id: string;
    suggestions: TaskSuggestion[];
    requestId: string;
    mock: boolean;
  };
  message?: string;
}

export interface GetTasksParams {
  status?: 'pending' | 'in_progress' | 'completed' | 'overdue';
  priority?: 'high' | 'medium' | 'low';
  category?: string;
  patient_id?: string;
  assigned_to?: string;
  clinic_id?: string;
  due_date_start?: string;
  due_date_end?: string;
  source?: 'manual' | 'ai_generated';
  page?: number;
  limit?: number;
}

export const taskService = {
  /**
   * Search patients for task creation
   */
  searchPatients: (search: string, clinicId?: string): Promise<ApiResponse<{ patients: TaskSearchPatient[] }>> => {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (clinicId) queryParams.append('clinic_id', clinicId);

    const queryString = queryParams.toString();
    return api.get(`/tasks/patients/search${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Create a new task
   */
  createTask: (taskData: Partial<Task>): Promise<ApiResponse<Task>> => {
    return api.post('/tasks', taskData);
  },

  /**
   * Get tasks with optional filters
   */
  getTasks: (params?: GetTasksParams): Promise<ApiResponse<{
    tasks: Task[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  }>> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.patient_id) queryParams.append('patient_id', params.patient_id);
    if (params?.assigned_to) queryParams.append('assigned_to', params.assigned_to);
    if (params?.clinic_id) queryParams.append('clinic_id', params.clinic_id);
    if (params?.due_date_start) queryParams.append('due_date_start', params.due_date_start);
    if (params?.due_date_end) queryParams.append('due_date_end', params.due_date_end);
    if (params?.source) queryParams.append('source', params.source);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    return api.get(`/tasks${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get single task by ID
   */
  getTask: (taskId: string): Promise<ApiResponse<Task>> => {
    return api.get(`/tasks/${taskId}`);
  },

  /**
   * Update task
   */
  updateTask: (taskId: string, updates: Partial<Task>): Promise<ApiResponse<Task>> => {
    return api.patch(`/tasks/${taskId}`, updates);
  },

  /**
   * Delete task (soft delete)
   */
  deleteTask: (taskId: string): Promise<ApiResponse<void>> => {
    return api.delete(`/tasks/${taskId}`);
  },

  /**
   * Bulk create tasks (for AI suggestions)
   */
  bulkCreateTasks: (tasks: Partial<Task>[]): Promise<ApiResponse<{
    tasks: Task[];
    count: number;
  }>> => {
    return api.post('/tasks/bulk', { tasks });
  },

  /**
   * Get all tasks for a provider
   */
  getProviderTasks: (providerId: string, filters?: {
    status?: string;
    due_date_start?: string;
    due_date_end?: string;
  }): Promise<ApiResponse<{
    tasks: Task[];
    count: number;
  }>> => {
    const queryParams = new URLSearchParams();
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.due_date_start) queryParams.append('due_date_start', filters.due_date_start);
    if (filters?.due_date_end) queryParams.append('due_date_end', filters.due_date_end);

    const queryString = queryParams.toString();
    return api.get(`/tasks/provider/${providerId}${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get all tasks for a patient
   */
  getPatientTasks: (patientId: string): Promise<ApiResponse<{
    tasks: Task[];
    count: number;
  }>> => {
    return api.get(`/tasks/patient/${patientId}`);
  },

  /**
   * Get AI task suggestions for completed encounter
   */
  getTaskSuggestions: (encounterId: string, encounterData: {
    medications?: Array<{ name: string; [key: string]: any }>;
    labs_ordered?: string[];
    is_new_patient?: boolean;
    medication_changes?: string[];
    chief_complaint?: string;
    needs_documentation?: boolean;
    encounter_type?: string;
  }): Promise<TaskSuggestionsResponse> => {
    return api.post(`/intellihealth/encounters/${encounterId}/task-suggestions`, {
      encounter_data: encounterData,
    });
  },

  /**
   * Mark task as complete
   */
  markComplete: (taskId: string): Promise<ApiResponse<Task>> => {
    return api.patch(`/tasks/${taskId}`, { status: 'completed' });
  },
};
